import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { en, type TranslationKey } from "./en.js";
import { fr } from "./fr.js";

export const LOCALES = ["fr", "en"] as const;
export type Locale = (typeof LOCALES)[number];

/** French is the default: this is a French household's game. */
export const DEFAULT_LOCALE: Locale = "fr";

const STORAGE_KEY = "hootapp_locale";

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { en, fr };

export const LOCALE_LABEL_KEY: Record<Locale, TranslationKey> = {
  en: "common.english",
  fr: "common.french",
};

function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

function readStoredLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private windows and blocked storage both throw on read; the default is
    // a perfectly good answer.
  }
  return DEFAULT_LOCALE;
}

/** Substitutes `{name}` placeholders. Unknown names are left alone. */
export function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

export type Translate = (
  key: TranslationKey,
  params?: Record<string, string | number>,
) => string;

interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
}

/** Exported for infrastructure components that must also render on crashes. */
export const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /** Overrides the stored preference. Intended for tests. */
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? readStoredLocale());

  useEffect(() => {
    // Screen readers and browser translation both key off this, and it is
    // wrong on every page until it is set.
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies to this session even if it cannot be kept.
    }
  }, []);

  const value = useMemo<LanguageContextValue>(() => {
    const dictionary = DICTIONARIES[locale];
    return {
      locale,
      setLocale,
      t: (key, params) => interpolate(dictionary[key] ?? en[key], params),
    };
  }, [locale, setLocale]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used inside LanguageProvider");
  return context;
}

/** The translate function on its own, which is all most components want. */
export function useT(): Translate {
  return useLanguage().t;
}
